import React, { useState } from "react";
import { X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Athlete-side modal for logging a test score.
 * Reused by AthleteTestLibrary + MessagesThread (Coach Chat log-score chip).
 */
export default function LogScoreModal({ test, onClose, onSaved }) {
  const [form, setForm] = useState({
    value: "",
    unit: "",
    notes: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (form.value === "" || isNaN(parseFloat(form.value))) {
      toast.error("Enter a numeric value");
      return;
    }
    setSaving(true);
    try {
      await api.post("/athlete/test-results", {
        test_id: test.test_id,
        value: parseFloat(form.value),
        unit: form.unit || null,
        notes: form.notes || null,
        date: form.date || null,
      });
      toast.success(`Logged: ${form.value}${form.unit ? " " + form.unit : ""}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="log-score-modal"
    >
      <div
        className="bg-surface-DEFAULT border border-white/10 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex items-start justify-between">
          <div>
            <div className="label-tiny text-brand-accent">// LOG SCORE</div>
            <div className="font-heading text-lg">{test.name}</div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-heat"
            data-testid="log-score-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-tiny block mb-1">VALUE*</label>
              <input
                type="number"
                step="0.01"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="e.g. 42.5"
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="log-score-value"
                autoFocus
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">UNIT</label>
              <input
                type="text"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="e.g. cm, sec, reps"
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
                data-testid="log-score-unit"
              />
            </div>
          </div>
          <div>
            <label className="label-tiny block mb-1">DATE</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="log-score-date"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">NOTES</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="conditions, feel, etc."
              className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="log-score-notes"
            />
          </div>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="border border-white/10 px-4 py-2 text-xs label-tiny tracking-widest hover:border-white/40 transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="log-score-submit"
          >
            <CheckCircle size={12} weight="bold" /> {saving ? "SAVING..." : "SAVE SCORE"}
          </button>
        </div>
      </div>
    </div>
  );
}
